<template>
  <div class="app-container" id="爱漫调APP">
    <!-- 渐变蒙版背景 -->
    <div class="gradient-overlay"></div>

    <!-- 主内容区 -->
    <div class="modern-container">
      <!-- 标题区 -->
      <div class="modern-header">
        <div class="brand-logo">
        </div>
        <h1 class="gradient-title">
          <span class="english-text">AI-Powered Business Platform</span>
          <span class="chinese-text">Imandiao企业私有大模型——您的智能知识管理专家</span>
        </h1>
        <div class="subtitle">
          <p class="main-desc">革新企业知识管理，AI驱动未来</p>
          <p class="detail-desc">爱漫调企业私有大模型是一款专为企业打造的全功能智能知识管理平台。它集智能检索、AI对话、文档管理于一体，助您高效管理海量知识资产，释放AI潜能！</p>
        </div>
      </div>

      <!-- 下载中心 - 移动到这里 -->
      <div class="download-center">
        <!-- 左侧功能描述 -->
        <div class="feature-preview">
          <div class="call-to-action">
            <h3>{{ currentIntroduction.title }}</h3>
            <p class="subtitle-text">{{ currentIntroduction.subtitle }}</p>
            <div class="feature-list-container">
              <ul class="dynamic-feature-list">
                <li v-for="(feature, index) in currentIntroduction.features" :key="index">
                  {{ feature }}
                </li>
              </ul>
            </div>
          </div>
        </div>

        <!-- 右侧下载操作 -->
        <div class="download-operations">
          <div class="platform-selector">
            <div
              v-for="(item, index) in versions"
              :key="index"
              class="platform-card"
              :class="{ 'active': selectedVersion === item.value }"
              @click="selectedVersion = item.value"
            >
              <i :class="['platform-icon', item.icon]"></i>
              <span>{{ item.label }}</span>
              <div class="hover-effect"></div>
            </div>
          </div>

          <div
            class="download-btn"
            @click="handleDownload"
            :class="{ 'downloading': isDownloading }"
          >
            <div class="btn-content">
              <template v-if="!isDownloading">
                <i class="iconfont icon-download"></i>
                <span>立即下载 {{ selectedVersionInfo.size }}</span>
              </template>
              <template v-else>
                <div class="loading-spinner"></div>
                <span>下载中 {{ downloadProgress }}%</span>
              </template>
            </div>
            <div class="version-info">当前版本 {{ selectedVersionInfo.version }}</div>
          </div>
        </div>
      </div>

      <!-- 核心优势区 -->
      <div class="core-advantages">
        <h2 class="section-title">核心优势</h2>
        <div class="advantage-grid">
          <div
            v-for="(item, index) in coreAdvantages"
            :key="index"
            class="advantage-card"
            :style="{ '--delay': index * 0.1 + 's' }"
          >
            <div class="card-header">
              <h3>{{ item.title }}</h3>
            </div>
            <div class="card-content">
              <p class="main-desc">{{ item.mainDesc }}</p>
              <ul class="feature-list">
                <li v-for="(feature, i) in item.features" :key="i">{{ feature }}</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <!-- 功能一览区 -->
      <div class="function-overview">
        <h2 class="section-title">功能一览</h2>
        <div class="function-grid">
          <div
            v-for="(func, index) in functions"
            :key="index"
            class="function-item"
          >
            <div class="function-content">
              <h4>{{ func.name }}</h4>
              <p class="func-desc">{{ func.desc }}</p>
              <ul class="func-features">
                <li v-for="(feature, i) in func.features" :key="i">{{ feature }}</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <!-- 适用场景区 -->
      <div class="use-cases">
        <h2 class="section-title">适用场景</h2>
        <div class="cases-grid">
          <div
            v-for="(useCase, index) in useCases"
            :key="index"
            class="case-card"
          >
            <h4>{{ useCase.title }}</h4>
            <p>{{ useCase.desc }}</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Message } from 'element-ui'

export default {
  data () {
    return {
      isDownloading: false,
      selectedVersion: 'winclient',
      versionIntroductions: {
        winclient: {
          title: 'Windows 客户端',
          subtitle: '极简高效的企业知识管理工具',
          features: [
            '极简文档管理：拖拽上传即自动完成解析、分块、向量化，支持文件夹分级与批量操作，知识库搭建零学习成本',
            '智能对话：流式响应秒级反馈，用企业私有文档精准问答，上下文记忆连贯自然',
            '全流程协同：文档状态可视化管理（待审核/已批准/已拒绝），管理员一键审批，团队知识高效流转',
            '离线也能畅用AI：连接服务端后随时随地使用AI，离线场景也能稳定运行'
          ]
        },
        winserver: {
          title: 'Windows 服务端',
          subtitle: '企业级智能知识引擎核心',
          features: [
            '智能知识引擎：自动解析PDF/Word/Excel/PPT等全格式文档，语义分块+向量存储，构建企业专属动态知识库，让隐性知识显性化',
            '企业级安全架构：JWT动态认证+设备白名单绑定+细粒度角色权限，数据本地化存储，杜绝外部泄露风险',
            'RAG精准问答：融合向量检索与大模型推理，从海量文档中快速定位答案并关联原文来源，回答依据可追溯',
            '零门槛部署：默认搭载Qwen2.5-32B模型，单台电脑即可使用(显存至少24GB)，一键启动即享企业级AI服务，无需专业知识'
          ]
        },
        winupdate: {
          title: 'Windows 升级端',
          subtitle: '轻松升级至千亿参数模型',
          features: [
            '零感知升级体验：无需复杂配置，一键将系统无缝升级至Qwen2.5-72B千亿参数模型，全程可视化进度反馈',
            '智能资源适配：自动检测磁盘空间与硬件配置，断点续传技术保障升级不中断，网络波动无影响',
            '能力全面升级：优化响应更精准，基于企业私有知识库的回答更贴合业务场景，语言表达自然流畅，提升专业沟通效率',
            '企业级效能优化：在常规硬件环境下实现高效运行，平衡性能与部署成本，保障稳定使用体验'
          ]
        }
      },
      coreAdvantages: [
        {
          title: '智能问答，精准高效',
          mainDesc: '基于RAG架构（检索增强生成），结合向量检索与大语言模型深度协同，提供上下文感知的精准回答：',
          features: [
            '集成Ollama框架，支持多模型灵活切换（默认搭载qwen2.5-32b，可升级至72b）',
            '实时流式对话体验，像与专家面对面交流般自然',
            '回答自动关联文档来源，有据可依更可信'
          ]
        },
        {
          title: '多格式文档处理，一键构建知识库',
          mainDesc: '全方位覆盖企业文档场景，从上传到应用全程自动化：',
          features: [
            '支持PDF、Word、Excel、PowerPoint、TXT等主流格式',
            '语义感知的智能分块算法，保留内容逻辑关联',
            '自动向量化存储与索引构建，让知识"活"起来'
          ]
        },
        {
          title: '企业级安全，数据无忧',
          mainDesc: '多重安全机制构筑坚固防线，保障核心知识资产：',
          features: [
            'JWT认证与动态Token刷新，确保身份安全',
            '精细化角色权限管理，管理员与普通用户权限严格分离',
            '基于机器ID的设备白名单，杜绝未授权访问',
            '强密码策略与账户锁定机制，搭配端到端加密与完备审计日志'
          ]
        },
        {
          title: '流畅体验，零门槛部署与管理',
          mainDesc: '全链路优化的使用与运维体验：',
          features: [
            '支持桌面端应用运行，操作界面响应式适配',
            '支持拖拽上传、批量处理、实时进度显示，操作流畅',
            '内置Ollama模型自动安装、更新与管理，无需专业知识也能轻松使用',
            '自动数据库备份与恢复功能，数据安全有保障'
          ]
        }
      ],
      functions: [
        {
          name: '智能对话系统',
          desc: '',
          features: [
            '上下文感知对话，支持多轮连续交互',
            '流式响应技术，实时显示回答过程',
            '与知识库深度联动，精准引用文档'
          ]
        },
        {
          name: '高级语义检索',
          desc: '',
          features: [
            '基于向量相似度的智能语义检索，超越传统关键词限制',
            '多层级检索结果重排序，优先展示高关联内容',
            '智能上下文扩展，自动发现相关联知识'
          ]
        },
        {
          name: '文档全生命周期管理',
          desc: '',
          features: [
            '从上传、解析、分块到存储的全流程自动化处理',
            '文件夹层级分类与版本控制，支持文档状态管理（待审核/已批准/已拒绝）',
            '完整的文件生命周期追踪与权限控制'
          ]
        },
        {
          name: '系统监控与管理',
          desc: '',
          features: [
            '实时系统状态监控与服务管理',
            '自动部署与模型更新，减少人工干预',
            'Windows桌面应用支持，运行稳定流畅'
          ]
        }
      ],
      useCases: [
        {
          title: '企业知识库',
          desc: '快速构建、管理海量文档，员工随时精准检索所需信息'
        },
        {
          title: '智能客服',
          desc: '基于知识库的标准化问答，提升客户服务效率与一致性'
        },
        {
          title: '团队协作',
          desc: '权限分级管理确保信息安全共享，加速跨部门协作'
        },
        {
          title: '研究与分析',
          desc: '高效处理多格式资料，AI辅助提炼核心观点与生成洞察'
        }
      ],
      versions: [
        {
          label: 'Windows 客户端',
          value: 'winclient',
          icon: 'icon-windows',
          version: 'v1.0',
          size: '155MB'
        },
        {
          label: 'Windows 服务端',
          value: 'winserver',
          icon: 'icon-android',
          version: 'v1.0',
          size: '348MB'
        },
        {
          label: 'Windows 升级端',
          value: 'winupdate',
          icon: 'icon-android',
          version: 'v1.0',
          size: '177MB'
        }
      ],
      downloadMap: {
        winclient: {
          url: 'https://imandiao.oss-cn-beijing.aliyuncs.com/Imandiao_AI/client_installer.exe',
          name: 'Client_installer.exe'
        },
        winserver: {
          url: 'https://imandiao.oss-cn-beijing.aliyuncs.com/Imandiao_AI/server_installer.exe',
          name: 'Server_installer.exe'
        },
        winupdate: {
          url: 'https://imandiao.oss-cn-beijing.aliyuncs.com/Imandiao_AI/qwen2.5_72b_update.exe',
          name: 'qwen2.5_72b.exe'
        }
      }
    }
  },
  methods: {
    async handleDownload () {
      if (this.isDownloading) return

      try {
        this.isDownloading = true
        const {
          url,
          name
        } = this.downloadMap[this.selectedVersion]

        const link = document.createElement('a')
        link.href = url
        link.download = name
        link.click()

        Message.success('下载已开始，请稍候...')
      } catch (error) {
        Message.error('下载失败：' + error.message)
      } finally {
        this.isDownloading = false
      }
    }
  },
  computed: {
    selectedVersionInfo () {
      return this.versions.find(v => v.value === this.selectedVersion)
    },
    currentIntroduction () {
      return this.versionIntroductions[this.selectedVersion]
    }
  }
}
</script>

<style scoped>
@import "@/assets/styles/common.css";

/* 基础重置 */
.app-container {
  min-height: 100vh;
  background: linear-gradient(135deg, #2b2d42 0%, #1a1b2f 100%);
  color: #fff;
  position: relative;
}

.gradient-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: radial-gradient(circle at 80% 20%, rgba(230, 0, 18, 0.15) 0%, transparent 60%);
}

.modern-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 80px 60px;
  position: relative;
  z-index: 1;
}

/* 标题区 */
.modern-header {
  text-align: center;
  margin-bottom: 100px;
  max-width: 900px;
  margin-left: auto;
  margin-right: auto;
}

.brand-logo img {
  height: 60px;
  margin-bottom: 30px;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3));
}

.gradient-title {
  font-family: 'PingFang SC', sans-serif;
  margin-bottom: 50px;
}

.english-text {
  display: block;
  font-size: 3rem;
  background: linear-gradient(90deg, #ff6b6b, #ff8e8e);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  letter-spacing: 3px;
  margin-bottom: 20px;
  line-height: 1.4;
  font-weight: 600;
}

.chinese-text {
  font-size: 2.4rem;
  font-weight: 400;
  opacity: 0.95;
  line-height: 1.5;
  word-break: keep-all;  /* 防止中文词汇被拆分 */
  white-space: nowrap;   /* 防止标题换行 */
}

.subtitle {
  max-width: 800px;
  margin: 0 auto;
}

.subtitle .main-desc {
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 25px;
  background: linear-gradient(90deg, #ff6b6b, #ff8e8e);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  line-height: 1.6;
}

.subtitle .detail-desc {
  font-size: 1.2rem;
  line-height: 1.8;
  opacity: 0.85;
  color: #e8e8e8;
}

/* 章节标题 */
.section-title {
  font-size: 2.2rem;
  text-align: center;
  margin-bottom: 60px;
  background: linear-gradient(90deg, #ff6b6b, #ff8e8e);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  font-weight: 700;
  position: relative;
}

.section-title::after {
  content: '';
  position: absolute;
  bottom: -15px;
  left: 50%;
  transform: translateX(-50%);
  width: 80px;
  height: 3px;
  background: linear-gradient(90deg, #ff6b6b, #ff8e8e);
  border-radius: 2px;
}

/* 核心优势区 */
.core-advantages {
  margin-bottom: 120px;
}

.advantage-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 40px 50px;
  max-width: 1200px;
  margin: 0 auto;
}

.advantage-card {
  background: rgba(255, 255, 255, 0.06);
  border-radius: 24px;
  padding: 40px;
  backdrop-filter: blur(15px);
  border: 1px solid rgba(255, 255, 255, 0.12);
  transform: translateY(20px);
  opacity: 0;
  animation: cardEntrance 0.6s ease-out forwards;
  transition: all 0.4s ease;
  position: relative;
  overflow: hidden;
}

.advantage-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, #ff6b6b, #ff8e8e);
}

.advantage-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 25px 50px rgba(255, 107, 107, 0.15);
  background: rgba(255, 255, 255, 0.08);
}

@keyframes cardEntrance {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.card-header {
  margin-bottom: 30px;
}

.advantage-card h3 {
  font-size: 1.5rem;
  margin: 0;
  font-weight: 700;
  line-height: 1.3;
}

.card-content .main-desc {
  font-size: 1.1rem;
  line-height: 1.8;
  margin-bottom: 20px;
  color: #e8e8e8;
}

.feature-list {
  padding: 0;
  margin: 0;
}

.feature-list li {
  font-size: 1rem;
  padding: 10px 0;
  opacity: 0.9;
  position: relative;
  color: #d8d8d8;
  line-height: 1.6;
}

/* 功能一览区 */
.function-overview {
  margin-bottom: 120px;
}

.function-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 30px 40px;
  max-width: 1000px;
  margin: 0 auto;
}

.function-item {
  padding: 35px;
  background: rgba(255, 255, 255, 0.04);
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.4s ease;
  position: relative;
}

.function-item::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 2px;
  background: linear-gradient(90deg, transparent, #ff6b6b, transparent);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.function-item:hover::before {
  opacity: 1;
}

.function-item:hover {
  background: rgba(255, 255, 255, 0.08);
  transform: translateY(-5px);
  box-shadow: 0 15px 30px rgba(0, 0, 0, 0.2);
}

.function-content h4 {
  font-size: 1.3rem;
  margin: 0 0 12px 0;
  font-weight: 700;
  line-height: 1.3;
}

.function-content p {
  font-size: 1rem;
  line-height: 1.7;
  margin: 0;
  opacity: 0.85;
  color: #d8d8d8;
}

/* 功能区域特殊样式 */
.func-desc {
  font-size: 1rem;
  line-height: 1.7;
  margin: 0 0 15px 0;
  opacity: 0.85;
  color: #d8d8d8;
}

.func-features {
  padding: 0;
  margin: 0;
}

.func-features li {
  font-size: 0.95rem;
  padding: 8px 0;
  opacity: 0.85;
  position: relative;
  color: #d0d0d0;
  line-height: 1.6;
}

/* 适用场景区 */
.use-cases {
  margin-bottom: 120px;
}

.cases-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 40px 50px;
  max-width: 1000px;
  margin: 0 auto;
}

.case-card {
  text-align: center;
  padding: 50px 40px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 24px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  transition: all 0.4s ease;
  position: relative;
  overflow: hidden;
}

.case-card::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 0;
  height: 3px;
  background: linear-gradient(90deg, #ff6b6b, #ff8e8e);
  transition: width 0.4s ease;
}

.case-card:hover::after {
  width: 80%;
}

.case-card:hover {
  transform: translateY(-10px);
  box-shadow: 0 20px 40px rgba(255, 107, 107, 0.2);
  background: rgba(255, 255, 255, 0.08);
}

.case-card h4 {
  font-size: 1.4rem;
  margin-bottom: 18px;
  font-weight: 700;
  line-height: 1.3;
}

.case-card p {
  font-size: 1.1rem;
  line-height: 1.7;
  opacity: 0.85;
  color: #d8d8d8;
  margin: 0;
}

/* 下载中心 */
.download-center {
  display: grid;
  grid-template-columns: 1fr 450px;
  gap: 80px;
  background: rgba(255, 255, 255, 0.04);
  border-radius: 32px;
  padding: 60px;
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.15);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  max-width: 1200px;
  margin: 0 auto 120px auto; /* 添加底边距保持一致性 */
}

.feature-preview {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.call-to-action {
  padding: 40px;
  background: rgba(255, 107, 107, 0.12);
  border-radius: 24px;
  border: 2px solid rgba(255, 107, 107, 0.25);
  text-align: left; /* 改为左对齐，更适合列表显示 */
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease; /* 添加过渡动画 */
}

.call-to-action::before {
  content: '';
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: radial-gradient(circle, rgba(255, 107, 107, 0.1) 0%, transparent 70%);
  animation: rotate 20s linear infinite;
}

@keyframes rotate {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.call-to-action h3 {
  font-size: 1.6rem;
  margin-bottom: 15px;
  background: linear-gradient(90deg, #ff6b6b, #ff8e8e);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  position: relative;
  z-index: 1;
  font-weight: 700;
  text-align: center;
}

.subtitle-text {
  font-size: 1.1rem;
  line-height: 1.6;
  opacity: 0.9;
  margin-bottom: 25px;
  position: relative;
  z-index: 1;
  color: #f0f0f0;
  text-align: center;
  font-weight: 500;
}

.feature-list-container {
  position: relative;
  z-index: 1;
}

.dynamic-feature-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.dynamic-feature-list li {
  font-size: 1rem;
  line-height: 1.7;
  margin-bottom: 15px;
  padding-left: 20px;
  position: relative;
  color: #e8e8e8;
  opacity: 0.9;
}

.dynamic-feature-list li::before {
  content: "•";
  color: #ff6b6b;
  font-weight: bold;
  position: absolute;
  left: 0;
  font-size: 1.2rem;
}

.dynamic-feature-list li:last-child {
  margin-bottom: 0;
}

/* 添加切换时的动画效果 */
.call-to-action * {
  transition: opacity 0.3s ease;
}

.download-operations {
  display: flex;
  flex-direction: column;
  justify-content: center;
}

/* 平台选择 */
.platform-selector {
  display: grid;
  gap: 20px;
  margin-bottom: 40px;
}

.platform-card {
  position: relative;
  background: rgba(255, 255, 255, 0.06);
  border-radius: 16px;
  padding: 25px;
  cursor: pointer;
  transition: all 0.3s ease;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.platform-card.active {
  background: linear-gradient(45deg, #ff6b6b 30%, #ff8e8e 100%);
  box-shadow: 0 10px 25px rgba(255, 107, 107, 0.4);
}

.platform-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 20px rgba(255, 255, 255, 0.1);
}

.hover-effect {
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg,
  transparent,
  rgba(255, 255, 255, 0.15),
  transparent);
  transition: left 0.6s;
}

.platform-card:hover .hover-effect {
  left: 100%;
}

.platform-icon {
  font-size: 28px;
  margin-right: 15px;
  vertical-align: middle;
}

/* 下载按钮 */
.download-btn {
  background: linear-gradient(45deg, #ff6b6b, #ff8e8e);
  border-radius: 16px;
  padding: 30px;
  text-align: center;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;
  box-shadow: 0 10px 25px rgba(255, 107, 107, 0.3);
}

.download-btn:not(.downloading):hover {
  transform: scale(1.02);
  box-shadow: 0 15px 35px rgba(255, 107, 107, 0.5);
}

.btn-content {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.icon-download {
  font-size: 36px;
  animation: float 2s ease-in-out infinite;
}

@keyframes float {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-5px);
  }
}

.loading-spinner {
  width: 28px;
  height: 28px;
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.version-info {
  font-size: 1rem;
  opacity: 0.9;
  margin-top: 12px;
}

/* 响应式设计 */
@media (max-width: 1200px) {
  .modern-container {
    padding: 60px 40px;
  }

  .advantage-grid,
  .function-grid,
  .cases-grid {
    grid-template-columns: 1fr;
    gap: 30px;
  }

  .download-center {
    grid-template-columns: 1fr;
    gap: 50px;
    padding: 40px;
  }
}

@media (max-width: 768px) {
  .modern-container {
    padding: 40px 20px;
  }

  .english-text {
    font-size: 2.2rem;
  }

  .chinese-text {
    font-size: 1.8rem;
  }

  .section-title {
    font-size: 1.8rem;
  }

  .advantage-card,
  .function-item,
  .case-card {
    padding: 30px 25px;
  }

  .download-center {
    padding: 30px 20px;
    gap: 40px;
  }

  .call-to-action {
    padding: 30px;
  }
}

</style>
